import {IconButton} from '@primer/react'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useState, type ReactNode} from 'react'
import styles from './SuggestionFileDisclosure.module.css'

/**
 * Layout options shared by the disclosure and its wrappers (`AutomatedReviewDiffEntry`,
 * the batch panel). Opt-in so existing single-suggestion consumers' DOM is unchanged.
 */
export type SuggestionFileDisclosureLayoutProps = {
  initialCollapsed?: boolean
  /** Add the file path to the toggle's accessible name to disambiguate multiple toggles. */
  pathInLabel?: boolean
  /** Wrap the path in a span so long paths wrap instead of clipping in narrow containers. */
  wrapPath?: boolean
}

/**
 * Collapsible per-file suggestion card chrome shared by `AutomatedReviewDiffEntry`
 * (CCR) and the Manage batch panel's human suggestions. Owns the header (chevron +
 * mono path) and open/closed state; the caller supplies the body via `children`.
 */
export function SuggestionFileDisclosure({
  path,
  children,
  initialCollapsed = false,
  pathInLabel = false,
  wrapPath = false,
}: {
  path: string
  children: ReactNode
} & SuggestionFileDisclosureLayoutProps) {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed)
  const onToggleCollapsed = () => setIsCollapsed(prev => !prev)

  const toggleVerb = isCollapsed ? 'Open' : 'Close'
  const toggleLabel = pathInLabel ? `${toggleVerb} review comment for ${path}` : `${toggleVerb} review comment`

  return (
    <div className="d-flex flex-column overflow-hidden">
      <div
        className={clsx(
          'd-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono',
          styles.Header,
          isCollapsed && 'rounded-2',
        )}
      >
        <IconButton
          type="button"
          aria-label={toggleLabel}
          aria-expanded={!isCollapsed}
          icon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
          size="small"
          variant="invisible"
          onClick={onToggleCollapsed}
        />
        {wrapPath ? <span className={styles.Path}>{path}</span> : path}
      </div>
      {!isCollapsed && children}
    </div>
  )
}
